export const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

/**
 * Every engine words a failed dynamic import differently, so this list has grown one entry per
 * ticket as new wordings surfaced in Sentry. Add to it rather than chaining more conditions.
 */
const CHUNK_LOAD_MESSAGES = [
	'Failed to fetch dynamically imported module',      // Chrome
	'error loading dynamically imported module',        // Firefox
	'Importing a module script failed',                 // Safari
	"'text/html' is not a valid JavaScript MIME type",  // dynamic import served an HTML error page
	'Unable to preload CSS for',                        // Vite's own __vitePreload, for a chunk's CSS
];

/**
 * Detect a dynamic-import failure caused by a stale deploy.
 *
 * Every deploy empties `public/build`, so a browser still running the previous bundle requests
 * hashed assets that no longer exist. Note `sentry.js` uses this as a *drop* predicate — anything
 * matched here stops being reported, on the grounds that the reload recovers it.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
	const message = error?.message;

	if (typeof message === 'string' && CHUNK_LOAD_MESSAGES.some((wording) => message.includes(wording))) {
		return true;
	}

	// webpack-style name, from third-party bundles we do not build.
	return error?.name === 'ChunkLoadError';
}

/**
 * The asset a chunk error failed to load, read off its message.
 *
 * Chrome and Firefox name the module URL and Vite names the stylesheet. Safari ("Importing a module
 * script failed.") and the MIME-type wording name nothing, so those return null.
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function chunkAssetUrl(error) {
	const message = error?.message;

	if (typeof message !== 'string') {
		return null;
	}

	const match = message.match(/https?:\/\/[^\s'")]+|\/[^\s'")]+\.(?:m?js|css)\b/);

	return match ? match[0].replace(/[.,;]+$/, '') : null;
}

/**
 * Set once a chunk error has been acted on, for the rest of this page lifetime.
 *
 * One failure reaches us twice in the SPA: vue-router calls its onError handlers synchronously and
 * then rejects the navigation with the same error, which surfaces as an unhandled rejection when the
 * caller did not catch it. Two failures can also land together - parallel lazy imports, or a chunk's
 * CSS and its JS. Before this latch the second arrival found the reload flag the first had just set
 * and reported "chunk reload exhausted" from the stale page, or, after a genuine exhausted report,
 * started yet another reload.
 *
 * Only the first decides. After a reload the next page is a new lifetime with a fresh latch; after
 * an exhausted report the page has already failed on a fresh load, so another reload would not help.
 * The trade-off: if something cancels the reload (a `beforeunload` prompt the user dismisses), this
 * page stays latched and later chunk errors go unhandled. Nothing in the app prompts on unload today.
 */
let chunkErrorHandled = false;

/**
 * Recover from a chunk error: reload onto the fresh manifest, or report that the reload did not help.
 *
 * Both handlers - the SPA's router.onError and each entry's unhandledrejection listener - go through
 * here so they share the page-lifetime latch above.
 *
 * @param {unknown} error
 * @param {() => string} resolveTarget Resolves the URL to reload onto. Only called for a chunk error.
 * @param {(target: string, error: unknown) => void} [onExhausted] Called instead of reloading when
 *                                                                 the budget is spent.
 */
export function handleChunkError(error, resolveTarget, onExhausted) {
	if (chunkErrorHandled || !isChunkLoadError(error)) {
		return;
	}

	chunkErrorHandled = true;

	const target = resolveTarget();

	if (!reloadOnChunkError(target)) {
		onExhausted?.(target, error);
	}
}

/**
 * Reload onto the fresh manifest, at most once per target.
 *
 * @param {string} reloadTarget URL to reload onto; doubles as the loop-guard key.
 * @returns {boolean} True if a reload was started. False means the one-shot budget for this target
 *                    was already spent, i.e. we reloaded and it still failed.
 */
export function reloadOnChunkError(reloadTarget) {
	if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === reloadTarget) {
		sessionStorage.removeItem(CHUNK_RELOAD_KEY);
		return false;
	}

	sessionStorage.setItem(CHUNK_RELOAD_KEY, reloadTarget);
	window.location.assign(reloadTarget);
	return true;
}

/**
 * Wire an entry point's `unhandledrejection` hook to the chunk-error recovery.
 *
 * Deliberately imports no Sentry, so this module stays a plain utility. Both entries call it through
 * bootstrap/chunk-reload.js, which passes an `onExhausted` that reports the case worth acting on —
 * the reload already happened and the user is still on a dead page.
 *
 * Also re-arms the latch when the page is restored from the back/forward cache. A page we reloaded
 * away from can come back that way with the latch still set, and would otherwise sit on its stale
 * chunks without reloading or reporting.
 *
 * @param {() => string} resolveTarget Resolves the URL to reload onto, at the moment of failure.
 * @param {(target: string, error: unknown) => void} [onExhausted] Called instead of reloading when
 *                                                                 the budget is spent.
 * @returns {() => void} Teardown that removes the listeners.
 */
export function registerChunkErrorHandler(resolveTarget, onExhausted) {
	const handleRejection = (event) => {
		handleChunkError(event.reason, resolveTarget, onExhausted);
	};

	const handlePageShow = (event) => {
		if (event.persisted) {
			chunkErrorHandled = false;
		}
	};

	window.addEventListener('unhandledrejection', handleRejection);
	window.addEventListener('pageshow', handlePageShow);

	return () => {
		window.removeEventListener('unhandledrejection', handleRejection);
		window.removeEventListener('pageshow', handlePageShow);
	};
}
